'use client';

import React from 'react';
import HeroHeading from '@components/ui/HeroHeading';
import LayoutContainer from '@components/ui/LayoutContainer';
import ContentBlock from '@components/blocks/ContentBlock';
import Button from '@components/buttons/BaseButton';
import Logo from '@components/images/Logo';
import {
  StyledSection,
  RedBoldText,
  FormatBox,
  FormatsGrid,
  DarkGreyButtonWrapper,
} from './styles';

const StudentAnimationShowcaseGuidelinesPage = () => {
  return (
    <>
      <HeroHeading>
        <Logo />
        <h1>Student Animation Showcase Guidelines</h1>
        <p style={{ fontSize: '2em', fontWeight: 'bold', color: 'black' }}>
          Please follow the guidelines below to prepare your submission.
        </p>
      </HeroHeading>

      <LayoutContainer hasSectionGaps>
        <StyledSection>
          <ContentBlock heading="" showImage={false}>
            <ul
              style={{
                listStyleType: 'disc',
                paddingLeft: '1.5rem',
                lineHeight: '1.8',
                fontSize: '1.1rem',
              }}
            >
              <li>
                We are only accepting submissions from films of{' '}
                <RedBoldText>graduating seniors</RedBoldText>.
              </li>

              <li>
                Submissions must not include:{' '}
                <RedBoldText>
                  Demo Reels, Trailers, Previews, or Game Cinematics.
                </RedBoldText>
              </li>

              <li>
                Add the List of the Student Films you are submitting{' '}
                <RedBoldText>in the order you want them to play</RedBoldText>.
              </li>

              <li>
                If there are multiple directors please{' '}
                <RedBoldText>
                  add only one director as the primary director
                </RedBoldText>
                .
              </li>

              <li>
                Include <RedBoldText>only one film per student</RedBoldText>.
              </li>

              <li>
                Submissions must be{' '}
                <RedBoldText>completed projects</RedBoldText>. Works in progress
                will not be accepted.
              </li>

              <li>
                Submissions must{' '}
                <RedBoldText>not include sexually explicit content</RedBoldText>
                , including nudity or graphic sexual themes.
              </li>
            </ul>
          </ContentBlock>
        </StyledSection>
        <StyledSection>
          <ContentBlock heading="File Formats" showImage={false}>
            <FormatsGrid>
              <FormatBox>
                <h3>Short Film</h3>
                <p>
                  <strong>Size:</strong> 1920x1080
                </p>
                <p>
                  <strong>Format:</strong> MP4
                </p>
                <p>
                  <strong>Compression:</strong> H264
                </p>
              </FormatBox>

              <FormatBox>
                <h3>Still Image</h3>
                <p>Include one still image for each film submitted</p>
                <p>
                  <strong>Size:</strong> 1920x1080
                </p>
                <p>
                  <strong>Format:</strong> JPEG
                </p>
              </FormatBox>

              <FormatBox>
                <h3>School Logo</h3>
                <p>
                  <strong>Size:</strong> 1000x1000
                </p>
                <p>
                  <strong>Format:</strong> PNG transparent background
                </p>
              </FormatBox>
            </FormatsGrid>
          </ContentBlock>
        </StyledSection>
        <StyledSection>
          <ContentBlock heading="Naming Your Film" showImage={false}>
            <ul
              style={{
                listStyleType: 'disc',
                paddingLeft: '1.5rem',
                lineHeight: '1.8',
                fontSize: '1.1rem',
              }}
            >
              <li>
                Name your video file to match your film title. For example, if
                your film is titled <RedBoldText>My Film</RedBoldText>, name the
                file <RedBoldText>my-film.mp4</RedBoldText>.
              </li>
            </ul>
          </ContentBlock>
        </StyledSection>
        <StyledSection>
          <ContentBlock heading="Uploading Instructions" showImage={false}>
            <ul
              style={{
                listStyleType: 'disc',
                paddingLeft: '1.5rem',
                lineHeight: '1.8',
                fontSize: '1.1rem',
              }}
            >
              <li>
                <RedBoldText>Do not upload uncompressed .mov files</RedBoldText>{' '}
                — they are often too large. Use H.264 and export as an .mp4 for
                the best quality-to-size ratio.
              </li>
              <li>
                Include all materials in <RedBoldText>one ZIP file</RedBoldText>
                . We will compile all of your student short films into a{' '}
                <RedBoldText>SINGLE SCHOOL SHOWREEL</RedBoldText>, adhering to
                the sequence you have provided.
              </li>
              <li>
                Please{' '}
                <a
                  target="_blank"
                  href="https://cdn.sanity.io/files/l8ll9i2k/production/b82385ce053ae98cc0c0df503b014f0521bb611e.pdf"
                >
                  Download the Performance/Screening Rights License Agreement
                </a>
                , fill out the form,{' '}
                <RedBoldText>and include it in your ZIP file</RedBoldText>.
              </li>
              <li>
                Upload <RedBoldText>one ZIP file</RedBoldText> with the
                requested materials inside. The maximum file size for the ZIP
                file is <RedBoldText>1 GB</RedBoldText>.
              </li>
              <li>
                If your file <RedBoldText>exceeds 1 GB</RedBoldText>, please
                split into multiple ZIP files, each{' '}
                <RedBoldText>no larger than 1 GB</RedBoldText>.
              </li>
            </ul>
          </ContentBlock>
        </StyledSection>
        <StyledSection>
          <ContentBlock
            heading="2026 Student Animation Showcase Submission Form"
            showImage={false}
          >
            <DarkGreyButtonWrapper>
              <Button
                actionType="link"
                theme="solid"
                link="https://form.jotform.com/230654188700152"
              >
                2026 Submission Form
              </Button>
            </DarkGreyButtonWrapper>
          </ContentBlock>
        </StyledSection>
      </LayoutContainer>
    </>
  );
};

export default StudentAnimationShowcaseGuidelinesPage;
